import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConvertTripInput,
  ConvertTripResult,
  CreateTripInput,
  ItemResponse,
  ListResponse,
  ReviewTripInput,
  TravelStatus,
  TripResponse,
  UpdateTripInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const TRAVEL_KEY = ['travel'] as const;

export interface TripFilters {
  status?: TravelStatus;
  employeeId?: string;
  year?: number;
  month?: number;
}

function buildQuery(filters: TripFilters): string {
  const params = new URLSearchParams({ pageSize: '500' });
  if (filters.status) params.set('status', filters.status);
  if (filters.employeeId) params.set('employeeId', filters.employeeId);
  if (filters.year) params.set('year', String(filters.year));
  if (filters.month) params.set('month', String(filters.month));
  return params.toString();
}

function useInvalidateTravel() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: TRAVEL_KEY });
}

export function useMyTrips(filters: TripFilters = {}) {
  return useQuery({
    queryKey: [...TRAVEL_KEY, 'mine', filters],
    queryFn: () => apiRequest<ListResponse<TripResponse>>(`/v1/trips?${buildQuery(filters)}`),
  });
}

export function useAllTrips(filters: TripFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...TRAVEL_KEY, 'all', filters],
    queryFn: () => apiRequest<ListResponse<TripResponse>>(`/v1/trips/all?${buildQuery(filters)}`),
    enabled,
  });
}

export function useCreateTrip() {
  const invalidate = useInvalidateTravel();
  return useMutation({
    mutationFn: (input: CreateTripInput) =>
      apiRequest<ItemResponse<TripResponse>>('/v1/trips', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useUpdateTrip() {
  const invalidate = useInvalidateTravel();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTripInput }) =>
      apiRequest<ItemResponse<TripResponse>>(`/v1/trips/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useDeleteTrip() {
  const invalidate = useInvalidateTravel();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ data: { id: string } }>(`/v1/trips/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useApproveTrip() {
  const invalidate = useInvalidateTravel();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReviewTripInput }) =>
      apiRequest<ItemResponse<TripResponse>>(`/v1/trips/${id}/approve`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useRejectTrip() {
  const invalidate = useInvalidateTravel();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReviewTripInput }) =>
      apiRequest<ItemResponse<TripResponse>>(`/v1/trips/${id}/reject`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

/**
 * Reise in Zeiteinträge wandeln (2026-08-20). Invalidiert neben den Reisen
 * auch die Zeiterfassungs-Queries — sonst zeigt das Zeitprotokoll die neuen
 * Einträge erst nach einem manuellen Neuladen.
 */
export function useConvertTrip() {
  const invalidate = useInvalidateTravel();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ConvertTripInput }) =>
      apiRequest<ItemResponse<ConvertTripResult>>(`/v1/trips/${id}/convert-to-time-entries`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['time-tracking'] });
    },
  });
}
